import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Alert, Card, Modal, Select, Space, Switch, Table, Tag, Typography } from 'antd'
import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons'
import { Button } from 'antd'
import {
  buildRoutePayload,
  fallbackWarning,
  gatewayLabel,
  interactionLabel,
  isLastEnabled,
  sortRails
} from './utils'

// One service's rails: what is offered, in what order, and who takes the money on each.
//
// **Saved per row, not per card.** A payments table with one Save button is a table where an
// operator who meant to move one rail discovers afterwards that they also moved another. Each
// control sends its own PATCH and the server answers with the row as it now stands, which is what
// the table then shows — so a refusal leaves the row exactly as the server last described it
// rather than as the operator hoped (companion correctness property 2).
const ServiceRouteCard = ({ service, country, rails, gateways, saving, onChange }) => {
  const [pending, setPending] = useState('')
  const ordered = sortRails(rails)

  const send = async (row, change) => {
    const payload = buildRoutePayload(row, change)
    if (!Object.keys(payload).length) return
    setPending(row.rail)
    await onChange(service, country, row, payload)
    setPending('')
  }

  const toggle = (row, enabled) => {
    if (!enabled && isLastEnabled(ordered, row.rail)) {
      // The one change on this page that can take a service offline for customers, so it is the
      // one that asks. Named in the sentence, because "are you sure?" is not information.
      Modal.confirm({
        title: `Turn off the last way to pay for ${service}?`,
        content: `Customers will not be able to pay for ${service}.`,
        okText: 'Turn it off',
        okButtonProps: { danger: true },
        onOk: () => send(row, { enabled: false })
      })
      return
    }
    send(row, { enabled })
  }

  const move = (row, delta) => {
    const next = Math.max(0, (row.position || 0) + delta)
    send(row, { position: next })
  }

  // antd's `render` is a function returning elements, which `react/display-name` reads as an
  // anonymous component. The same disable as `Tracking/AlertHistory.js`, for the same reason.
  /* eslint-disable react/display-name */
  const columns = [
    {
      title: 'Rail',
      dataIndex: 'label',
      key: 'rail',
      render: (label, row) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong>{label || row.rail}</Typography.Text>
          <Typography.Text type="secondary">{interactionLabel(row.interaction)}</Typography.Text>
        </Space>
      )
    },
    {
      title: 'Offered',
      dataIndex: 'enabled',
      key: 'enabled',
      render: (enabled, row) => (
        <Switch
          checked={enabled}
          loading={saving && pending === row.rail}
          onChange={value => toggle(row, value)}
        />
      )
    },
    {
      title: 'Order',
      key: 'position',
      render: (_text, row, index) => (
        <Space>
          <Button
            size="small"
            icon={<ArrowUpOutlined />}
            disabled={index === 0 || saving}
            onClick={() => move(row, -1)}
          />
          <Button
            size="small"
            icon={<ArrowDownOutlined />}
            disabled={index === ordered.length - 1 || saving}
            onClick={() => move(row, 1)}
          />
        </Space>
      )
    },
    {
      title: 'Gateway',
      dataIndex: 'gateway',
      key: 'gateway',
      render: (gateway, row) => (
        <Space direction="vertical" size={0} style={{ minWidth: '12rem' }}>
          <Select
            value={gateway}
            style={{ width: '100%' }}
            disabled={saving}
            onChange={value => send(row, { gateway: value })}
            // Only what the server says can serve THIS rail. A select offering `bank → paystack`
            // is a select that exists to produce a refusal.
            options={(row.gatewayOptions || []).map(key => ({
              value: key,
              label: key ? gatewayLabel(key, gateways) : `Default (${gatewayLabel(row.defaultGateway, gateways)})`
            }))}
          />
          <Typography.Text type="secondary">
            {`Using ${gatewayLabel(row.effectiveGateway, gateways)}`}
          </Typography.Text>
        </Space>
      )
    }
  ]
  /* eslint-enable react/display-name */

  return (
    <Card
      className="mb-5rem"
      title={
        <Space>
          <span>{service}</span>
          <Tag>{country}</Tag>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {ordered
          .map(row => ({ rail: row.rail, warning: fallbackWarning(row, gateways) }))
          .filter(entry => entry.warning)
          .map(entry => (
            <Alert key={entry.rail} type="warning" showIcon message={entry.warning} />
          ))}
        <Table
          size="small"
          rowKey="rail"
          columns={columns}
          dataSource={ordered}
          pagination={false}
          scroll={{ x: true }}
        />
      </Space>
    </Card>
  )
}

ServiceRouteCard.propTypes = {
  service: PropTypes.string,
  country: PropTypes.string,
  rails: PropTypes.array,
  gateways: PropTypes.array,
  saving: PropTypes.bool,
  onChange: PropTypes.func
}

export default ServiceRouteCard
